import { useForm } from "@mantine/form";
import { useContext } from "react";
import { Contract } from "@/types/contract";
import { Address, AddressType, AddressWithType } from "@/types/address";
import { validateCzechAddress, validateForeignAddress, validatePostalAddress } from "@/screens/Customer/validations/address";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import { initAddressData } from "@/utils/address";
import { equals } from "remeda";
import { useTranslation } from "@/i18n/client";

export interface AddressFormValues {
  isSame: boolean; // determines if MAIN and POSTAL are same
  isForeign: boolean; // only for MAIN, POSTAL cant be foreign
  MAIN: Address;
  POSTAL: Address;
  MAIN_FOREIGN: Address;
  isPostalForeign: boolean; // POSTAL for MS can be foreign
  POSTAL_FOREIGN: Address;
}

export const getAddress = (addresses: AddressWithType[], addressType: AddressType, czechCountryId: string) => {
  const address = addresses.find(address => address.type === addressType);
  return {
    addressPlaceCode: address?.addressPlaceCode ?? null,
    street: address?.street ?? "",
    countryCode: address?.countryCode ?? czechCountryId,
    houseNumber: address?.houseNumber ?? "",
    streetNumber: address?.streetNumber ?? "",
    city: address?.city ?? "",
    cityPart: address?.cityPart ?? "",
    zip: address?.zip ?? "",
    cadastralTerritory: address?.cadastralTerritory ?? "",
    cadastralTerritoryCode: address?.cadastralTerritoryCode ?? null,
    evidenceNumber: address?.evidenceNumber ?? "",
    nameCareOf: address?.nameCareOf ?? "",
  };
};

export const useFormAddress = (contract: Contract) => {
  const { czechCountryId } = useContext(EnumerationsContext);
  const { t } = useTranslation("validations");

  const mainAddress = getAddress(contract.addresses, "MAIN", czechCountryId);
  const postalAddress = getAddress(contract.addresses, "POSTAL", czechCountryId);
  const isForeign = mainAddress.countryCode !== czechCountryId && mainAddress.countryCode !== "";
  const isPostalForeign = postalAddress.countryCode !== czechCountryId && postalAddress.countryCode !== "";

  const form = useForm<AddressFormValues>({
    initialValues: {
      isSame: equals(mainAddress, postalAddress),
      isForeign,
      MAIN: isForeign ? initAddressData : mainAddress,
      MAIN_FOREIGN: isForeign ? mainAddress : initAddressData,
      POSTAL: isPostalForeign ? initAddressData : postalAddress,
      POSTAL_FOREIGN: isPostalForeign ? postalAddress : initAddressData,
      isPostalForeign,
    },
    validate: {
      MAIN: validateCzechAddress(t),
      MAIN_FOREIGN: validateForeignAddress(t, values => values.isForeign),
      POSTAL: validatePostalAddress(contract, t),
      POSTAL_FOREIGN: validateForeignAddress(t, values => values.isPostalForeign),
    },
  });

  form.watch("isSame", ({ value }) => {
    if (value) {
      form.setFieldValue("POSTAL", form.values.MAIN);
    } else {
      form.setFieldValue("POSTAL", initAddressData);
    }
  });

  return form;
};
